import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Company } from "@/types";
import { toast } from "sonner"
;

export function useCompanies() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchCompanies = async () => {
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('is_deleted', false) // Only fetch non-deleted companies
        .order('name', { ascending: true });
      
      if (error) {
        toast.error('Failed to load companies');
        return;
      }
      
      
      
      // Add a type guard for 'data' before calling .map
      let mappedCompanies: Company[] = [];
      if (Array.isArray(data)) {
        mappedCompanies = data.map((item: unknown): Company => {
          const company = item as Record<string, unknown>;
          return {
            id: company.id as string,
            name: company.name as string,
            contact_person: company.contact_person as string,
            contact_email: company.contact_email as string,
            contact_role: company.contact_role as string,
            domains: company.domains as string[],
            is_deleted: company.is_deleted as boolean,
          };
        });
      }
      
      setCompanies(mappedCompanies);
    } catch {
      toast.error('An error occurred while loading companies');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const addCompany = (company: Company) => {
    setCompanies([...companies, company]);
  };

  const updateCompany = (updatedCompany: Company) => {
    setCompanies(companies.map(c => c.id === updatedCompany.id ? updatedCompany : c));
  };

  const removeCompany = (id: string) => {
    setCompanies(companies.filter(c => c.id !== id));
  };

  return {
    companies,
    isLoading,
    fetchCompanies,
    addCompany,
    updateCompany,
    removeCompany
  };
}
